"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { useAppDispatch } from "@/store";
import { setUser } from "@/store/slices/authSlice";

import {
  CalendarChekIcon,
  DownopenArrowIcon,
  OFFlineIcon,
  LogOutIcon,
  MailIcon,
  PhoneIcon,
  RedactIcon,
} from "@/components/icons";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DrawerRight } from "@/components/ui/drawer-right";

import UserProfileDrawerContent from "@/app/users/[id]/components/UserProfileDrawerContent";
import { useGetUserActivityQuery } from "@/api/user/userApi";
import { cn } from "@/src/lib/utils";
import { getParticipantRoleLabel } from "@/app/assignments/components/sections/ParticipantsSection/utils";
import type { UserEntity } from "@/types/canonical";
import { useUiLanguageOptions } from "@/hooks/use-ui-language-options";
import { getUiLanguageOptionByLocale } from "@/shared/i18n/languages";

export type NavUserProfileDrawerProps = {
  user: UserEntity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
};

export function NavUserProfileDrawer({
  user,
  open,
  onOpenChange,
  onLogout,
}: NavUserProfileDrawerProps) {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const languageOptions = useUiLanguageOptions();

  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const editorRef = useRef<{ handleSave: () => void }>(null);

  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "—";
  const avatarUrl =
    user?.photo_thumb?.medium || user?.photo_thumb?.small || user?.photo || "";
  const roleLabelKey = user ? getParticipantRoleLabel(user as any) : "—";
  const roleLabel = roleLabelKey === "—" ? roleLabelKey : t(roleLabelKey);

  const {
    data: activityItems,
    isLoading: isActivityLoading,
    isError: isActivityError,
  } = useGetUserActivityQuery(
    { period: "week" },
    {
      // no need to fetch until the drawer is actually opened
      skip: !open,
    }
  );

  return (
    <DrawerRight
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);

        if (!nextOpen) {
          setIsEditing(false);
          setIsDetailsExpanded(false);
        }
      }}
      title={isEditing ? t('title-edit-user') : t('title-user-profile-alt')}
      size="lg"
      mode="sheet"
      className="max-md:min-w-full max-md:max-w-full max-md:w-full max-md:rounded-none"
      footer={
        isEditing ? (
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm bg-white px-5"
              onClick={() => setIsEditing(false)}
            >
              {t('btn-cancel')}
            </Button>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                className="rounded-sm bg-[#0059FF] text-white hover:bg-[#0047CC] inline-flex items-center gap-2 px-5"
                onClick={() => editorRef.current?.handleSave()}
                disabled={!user?.id}
              >
                <span>{t('btn-save')}</span>
              </Button>

              <Button
                type="button"
                className="rounded-sm bg-gradient-to-r from-[#0059FF] to-[#B00CFF] text-white hover:opacity-90 inline-flex items-center gap-2 px-5"
                onClick={onLogout}
              >
                <LogOutIcon size={18} className="text-white" />
                <span>{t('btn-logout-alt')}</span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {user?.group === "manager" && (
                <Button
                  type="button"
                  className="rounded-sm bg-[#0059FF] text-white hover:bg-[#0047CC] inline-flex items-center gap-2 px-5"
                  onClick={() => {
                    if (!user?.id) return;
                    setIsEditing(true);
                  }}
                  disabled={!user?.id}
                >
                  <RedactIcon size={18} className="text-white" />
                  <span>{t('btn-edit-user')}</span>
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="rounded-sm bg-white px-5"
                onClick={() => onOpenChange(false)}
              >
                {t('btn-cancel')}
              </Button>
            </div>
            <Button
              type="button"
              className="rounded-sm bg-gradient-to-r from-[#0059FF] to-[#B00CFF] text-white hover:opacity-90 inline-flex items-center gap-2 px-5"
              onClick={onLogout}
            >
              <LogOutIcon size={18} className="text-white" />
              <span>{t('btn-logout-alt')}</span>
            </Button>
          </div>
        )
      }
    >
      {isEditing ? (
        <div className="max-h-[calc(100vh-220px)] overflow-auto pe-1">
          <UserProfileDrawerContent
            ref={editorRef}
            mode="self-profile"
            showManagerRole={true}
            initialData={{
              id: user?.id,
              school_id: (user as any)?.school_id ?? null,
              photo: (user as any)?.photo ?? (avatarUrl || null),
              photo_thumb: (user as any)?.photo_thumb ?? {},
              color: (user as any)?.color ?? null,
              firstname: user?.firstname ?? "",
              lastname: user?.lastname ?? "",
              patronymic: (user as any)?.patronymic ?? null,
              email: user?.email ?? undefined,
              phone: (user as any)?.phone ?? undefined,
              username: (user as any)?.username ?? undefined,
              description: (user as any)?.description ?? null,

              school: (user as any)?.school ?? {},
              register_date: (user as any)?.register_date ?? null,
              last_activity_date: (user as any)?.last_activity_date ?? null,
              additional: (user as any)?.additional ?? {},
              is_online: Boolean((user as any)?.is_online ?? false),
              is_need_complete_challenge: Boolean(
                (user as any)?.is_need_complete_challenge ?? false
              ),
              is_multiple_schools: Boolean(
                (user as any)?.is_multiple_schools ?? false
              ),
              group: user?.group ?? null,
              user_type:
                user?.group === "teacher" ||
                  user?.group === "reviewer" ||
                  user?.group === "manager"
                  ? "teacher"
                  : "student",
            }}
            onSubmit={(updated: any) => {
              dispatch(setUser({ ...user, ...updated } as UserEntity));
              setIsEditing(false);
              onOpenChange(false);
            }}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Main card */}
          <div className="rounded-lg border bg-background p-4 shadow-md">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="shrink-0">
                <Avatar className="h-24 w-24 rounded-lg">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="rounded-lg">
                    {displayName?.slice(0, 1)}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="truncate text-xl font-semibold">
                    {displayName}
                  </div>
                  <Badge
                    variant="outline"
                    className="rounded-full px-3 py-1 text-sm text-muted-foreground"
                  >
                    {roleLabel}
                  </Badge>
                </div>

                <div className="mt-4 grid gap-3">
                  {(user as any)?.username && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <span className="text-muted-foreground">{t('label-iin-short')}</span>
                      <span className="text-foreground">
                        {(user as any).username}
                      </span>
                    </div>
                  )}

                  {(user as any)?.phone && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <PhoneIcon
                        size={18}
                        className="text-muted-foreground shrink-0"
                      />
                      <span className="text-foreground">
                        {(user as any).phone}
                      </span>
                    </div>
                  )}

                  {(user as any)?.email && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <MailIcon
                        size={18}
                        className="text-muted-foreground shrink-0"
                      />
                      <span className="text-foreground">
                        {(user as any).email}
                      </span>
                    </div>
                  )}

                  {(user as any)?.register_date && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <CalendarChekIcon
                        size={18}
                        className="text-muted-foreground shrink-0"
                      />
                      <span className="text-muted-foreground">
                        {t('label-registration-date')}
                      </span>
                      <span className="text-foreground">
                        {(user as any).register_date}
                      </span>
                    </div>
                  )}

                  {isDetailsExpanded && (
                    <div className="mt-2 grid gap-3 text-sm">
                      {(user as any)?.username && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <OFFlineIcon
                            size={18}
                            className="text-muted-foreground/50 shrink-0"
                          />
                          <span className="text-muted-foreground">{t('label-username')}</span>
                          <span className="text-foreground">
                            {(user as any).username}
                          </span>
                        </div>
                      )}

                      {(user as any)?.school?.name && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <OFFlineIcon
                            size={18}
                            className="text-muted-foreground/50 shrink-0"
                          />
                          <span className="text-muted-foreground">
                            {t('label-import-class-group-department')}
                          </span>
                          <span className="text-foreground">
                            {(user as any).school.name}
                          </span>
                        </div>
                      )}

                      {roleLabel !== "—" && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <OFFlineIcon
                            size={18}
                            className="text-muted-foreground/50 shrink-0"
                          />
                          <span className="text-muted-foreground">{t('label-position')}</span>
                          <span className="text-foreground">{roleLabel}</span>
                        </div>
                      )}

                      {(user as any)?.additional?.language && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <OFFlineIcon
                            size={18}
                            className="text-muted-foreground/50 shrink-0"
                          />
                          <span className="text-muted-foreground">{t('label-language')}</span>
                          <span className="text-foreground">
                            {getUiLanguageOptionByLocale(
                              languageOptions,
                              (user as any).additional.language
                            )?.label ?? String((user as any).additional.language)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[#0059FF] hover:opacity-80"
                    onClick={() => setIsDetailsExpanded((prev) => !prev)}
                    aria-expanded={isDetailsExpanded}
                  >
                    <DownopenArrowIcon
                      size={16}
                      className={cn(
                        "transition-transform",
                        isDetailsExpanded ? "rotate-180" : ""
                      )}
                    />
                    <span>{isDetailsExpanded ? t('label-collapse') : t('label-more')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Activity */}
          <div className="rounded-lg bg-muted/70 px-4 py-3 text-center font-semibold text-muted-foreground">
            {t('label-activity')}
          </div>

          <div className="rounded-lg border bg-background px-4 py-3 text-muted-foreground">
            {isActivityLoading ? (
              <div className="text-center">{t('label-loading')}</div>
            ) : isActivityError ? (
              <div className="text-center">{t('error-loading')}</div>
            ) : activityItems && activityItems.length > 0 ? (
              <div className="grid gap-2">
                {activityItems.map((item, idx) => (
                  <pre
                    key={idx}
                    className="max-h-64 overflow-auto rounded-md bg-muted/40 p-3 text-xs text-foreground"
                  >
                    {JSON.stringify(item, null, 2)}
                  </pre>
                ))}
              </div>
            ) : (
              <div className="text-center">{t('label-no-data-available')}</div>
            )}
          </div>
        </div>
      )}
    </DrawerRight>
  );
}
